import { ThreeToolsInstance, usePartDefinition } from "@project/emerge";
import type { AbstractPartDefinition, Sides } from "@project/emerge-types";
import { useMemo, useRef } from "react";
import * as THREE from "three";
import { DoubleSide } from "three";
import type { MiterType } from "../enum/MiterType";
import type { LTPartType } from "../enum/PartType";
import type { RackRailType } from "../interfaces/RackRailType";
import { ProfiledContourGeometry } from "../parts/ProfiledContourGeometry";
import { RectPropertiesBySide } from "../structure/RectProperties";

interface RackRailRendererProps
  extends AbstractPartDefinition<LTPartType.RackRail> {
  partSelection: RackRailType;
  points: [THREE.Vector3, THREE.Vector3];
  orientation: number;
  miterType: MiterType;
  cutReference: Sides;
}

export const RackRailRenderer = ({ id }: { id: string }) => {
  const partDef = usePartDefinition<RackRailRendererProps>({
    id,
    select: [
      "partSelection",
      "points",
      "orientation",
      "miterType",
      "cutReference",
      "position",
    ],
  });

  const meshRef = useRef<THREE.Mesh | null>(null);

  const { miterType, partSelection, points, cutReference, orientation } =
    partDef || {};
  const position = partDef?.position;
  const rectProps = cutReference ? RectPropertiesBySide[cutReference] : null;
  const horizontal = rectProps?.horizontal;
  const vertical = rectProps?.vertical;
  const ccw = false;

  const railThickness = partSelection?.spacing || 25;
  const materialThickness = 2.5;
  const pathPoints = useMemo(() => {
    if (
      !points ||
      points.length !== 2 ||
      !rectProps ||
      !horizontal ||
      !vertical ||
      !position
    )
      return [];
    const pts = ccw ? [points[1], points[0]] : [points[0], points[1]];
    const relativePoints = pts.map((p: THREE.Vector3) => {
      const v3 = new THREE.Vector3(p.x, p.y, p.z);
      const pos = new THREE.Vector3(position.x, position.y, position.z);
      v3.sub(pos);
      return v3;
    });
    return relativePoints.map(
      (p: THREE.Vector3) => new THREE.Vector2(p[vertical], p[horizontal]),
    );
  }, [points, horizontal, vertical, rectProps, position]);

  const lProfileShape = useMemo(() => {
    const profile2D: Array<[number, number]> = [
      [0, 0],
      [-railThickness, 0],
      [-railThickness, materialThickness],
      [-materialThickness, materialThickness],
      [-materialThickness, railThickness],
      [0, railThickness],
      [0, 0],
    ];

    return ThreeToolsInstance.pointsToShape(profile2D);
  }, [railThickness]);

  const geometry = useMemo(() => {
    if (!pathPoints || pathPoints.length < 2 || !lProfileShape) return null;
    const point0 = pathPoints[0];
    const point1 = pathPoints[1];
    if (!point0 || !point1) return null;

    const baseGeometry = new ProfiledContourGeometry(
      lProfileShape,
      pathPoints,
      miterType,
      ccw,
    );

    const pathDirection2D = new THREE.Vector2()
      .subVectors(
        new THREE.Vector2(point1.x, point1.y),
        new THREE.Vector2(point0.x, point0.y),
      )
      .normalize();

    const angle = THREE.MathUtils.degToRad(-90);
    const longitudinalAxis = new THREE.Vector3(
      pathDirection2D.x,
      pathDirection2D.y,
      0,
    ).normalize();

    const rotationMatrix = new THREE.Matrix4().makeRotationAxis(
      longitudinalAxis,
      angle,
    );
    baseGeometry.applyMatrix4(rotationMatrix);

    return baseGeometry;
  }, [lProfileShape, pathPoints, miterType]);

  if (!partDef?.points || partDef.points.length !== 2 || !geometry) return null;

  // React Three Fiber props that Biome doesn't recognize - these are valid props
  const groupRotationProps = {
    rotation: [Math.PI / 2, 0, orientation ?? 0] as [number, number, number],
  };
  const meshProps = {
    geometry: geometry,
  };
  const materialProps = {
    color: "#404040",
    metalness: 0.3,
    roughness: 0.6,
    side: DoubleSide,
  };

  return (
    <group>
      <group {...groupRotationProps}>
        <mesh ref={meshRef} {...meshProps}>
          <meshStandardMaterial {...materialProps} />
        </mesh>
      </group>
    </group>
  );
};
